import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import styled from 'styled-components';

import IssueIntro from '../components/IssueIntro';
import SectionDesktop from '../components/SectionDesktop';
import { AuthContext } from '../contexts/AuthContext';
import { DataContext } from '../contexts/DataContext';
import AppPage from '../layouts/AppPage';
import { COVER_URL, DEFAULT_TITLE, MAX_VISIBLE_ISSUES } from '../lib/settings';
import PageNotFound from './PageNotFound';
import { useParams } from 'react-router-dom';
import { useDevice } from '../contexts/DeviceContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { on } from 'events';
import { Helmet } from 'react-helmet-async';
import { metaTags } from '../lib/utils/metaTags';
const SectionMobile = React.lazy(() => import('../components/SectionMobile'));

export default function Main({isMain}) {
  const { isMobile } = useDevice();
  const { api } = useContext(AuthContext);
  const { issues, setIssues, currentIssueIndex, setCurrentIssueIndex, issueIds, setIssueIds, getIssue } = useContext(DataContext);
  const [notFound, setNotFound] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    if (currentIssueIndex !== null && issueIds && issueIds.length > 0) return;
    const getLatestIssueWithAllIds = async () => {
      try {
        const issueId = parseInt(id) || 'latest';
        const response = await api.getIssueWithPieces(issueId, true);
        const issueIds = response.data.issuesIds;
        setIssueIds(issueIds);
        const issue = response.data.issue;
        setCurrentIssueIndex(
          issueId === 'latest' ? issueIds.length - 1 : issueIds.indexOf(issueId)
        );
        setIssues({
          [issue.id]: {...issue}
        });
      } catch (error) {
        if (id && error.response?.status === 404) {
          setNotFound(true);
        }
      };
    };
    getLatestIssueWithAllIds();
  }, [api, issueIds, setIssues, currentIssueIndex, setCurrentIssueIndex, setIssueIds, setNotFound, id]);

  useEffect(() => {
    // preload latest issues
    if (!issueIds || issueIds.length === 0) return;
    const latestIssueIds = issueIds.slice(-MAX_VISIBLE_ISSUES);
    latestIssueIds.forEach(async (id) => {
      if (!issues[id]) {
        await getIssue(id);
      }
    });
  }, [issueIds, issues, getIssue]);

  useEffect(() => {
    if (!issueIds || issueIds.length === 0) return;
    if (currentIssueIndex === null || issueIds.includes(currentIssueIndex)) return;
    const issueId = issueIds[currentIssueIndex];
    if (issues[issueId]) return;
    getIssue(issueId)
  }, [currentIssueIndex, getIssue, issues, issueIds]);

  useEffect(() => {
    if (id && issueIds && issueIds.length > 0) {
      const index = issueIds.indexOf(parseInt(id));
      if (index !== -1) {
        setCurrentIssueIndex(index);
      }
      if (!issues[id]) {
        getIssue(id);
      }
    }
  }, [id, issueIds, setCurrentIssueIndex, getIssue, issues]);

  const latestIssueIds = useMemo(() => issueIds?.slice(-MAX_VISIBLE_ISSUES).reverse() || null, [issueIds]);

  const issuesToDisplay = useMemo(() => {
    if (!issueIds || !issues) return [];
    const location = window.location.pathname.split('/');
    const id = location.at(-1);
    if (isMain || latestIssueIds.includes(parseInt(id))) {
      return latestIssueIds.map(id => issues[id]);
    } else {
      return [issues[id]];
    }
  }, [issueIds, issues, latestIssueIds, isMain]);

  const onSlideChange = useCallback(async (swiper) => {
    const id = latestIssueIds[swiper.activeIndex];
    const currentIssueIndex = issueIds.indexOf(id);
    if (issues[id]) setCurrentIssueIndex(currentIssueIndex)
    else {
      await getIssue(id);
      setCurrentIssueIndex(currentIssueIndex);
    }
  }, [issueIds, getIssue, issues, setCurrentIssueIndex, latestIssueIds]);


  const swiperContainer = useCallback(({children}) => {
    return isMobile ? (
      <Swiper
        slidesPerView={1}
        loop={false}
        lazy={true}
        lazyPreloadPrevNext={1}
        onSlideChange={onSlideChange}
      >
        {children}
      </Swiper>
    ) : <SWIPER_CONTAINER>{children}</SWIPER_CONTAINER>
  }
  , [isMobile, onSlideChange]);

  const swiperSlide = useCallback((issue, index) => {
    if (!issueIds || !issue) return null;
    const slide = (
      <div
        className={`visible-issue ${issueIds[currentIssueIndex] === issue.id  ? "selected" : ""}`}
        key={index}
      >
        <IssueIntro 
          issueNum={issue.issue_number}
          date={issue.issue_date}
          imgSrc={issue.image_path}
          title={issue.title}
          annotation={issue.annotation}
        />
        <div className="sections">
          {issue.blocks?.map((block, index) => (
            isMobile ? 
              <React.Suspense fallback={null} key={index}>
                <SectionMobile key={index} {...block} />
              </React.Suspense>
            : <SectionDesktop key={index} {...block} />
          ))}
        </div>
      </div>
    )
    return isMobile ? <SwiperSlide key={index}>{slide}</SwiperSlide> : slide;
  }, [issueIds, currentIssueIndex, isMobile]);

  const MetaTags = useMemo(() => {
    if (!issuesToDisplay || issuesToDisplay.length === 0) return <></>
    const issue = issuesToDisplay[0];
    return metaTags({
      title: `${DEFAULT_TITLE}`,
      description: issue.annotation || '',
      image: issue.image_path,
      url: `https://chimeras.world/`,
    });
  }, [issuesToDisplay]);


  return (
    <>
    {notFound && <PageNotFound />}
    {!notFound && 
      <AppPage>
        {MetaTags}
        <MAIN>
          <div className="cover-container">
            <img src={COVER_URL} alt="Chimeras Cover" />
          </div>
          {issuesToDisplay.length > 0 && swiperContainer && swiperContainer({ children: (
            issuesToDisplay.map((issue, index) => (
              swiperSlide(issue, index)
            ))
          )})}
        </MAIN>
      </AppPage>
    }
    </>
  )
}

const MAIN = styled.div`
  & > .cover-container {
    background-color: var(blue);
    width: 100vw;
    min-height: calc(100vw * 0.5015625);

    img {
      width: 100%;
    }
  }

  & .sections {
    display: flex;
    flex-direction: column;
    gap: 90px;
  }
`;

const SWIPER_CONTAINER = styled.div`
  // TODO: add swiping effect?
  & > .visible-issue {
    display: none;
    flex: 0 0 calc(100vw);
    gap: 1.25rem;
  }

  & .visible-issue.selected {
    display: unset;
  }
`;